import classNames from 'classnames'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'

import { Translation } from 'cng-common/features/translations'
import { getQualityBand } from 'partner360/components/qualityIndicator'
import type { ICockpitExportCell } from 'partner360/features/financialCockpit/api'
import {
  getDeltaTone,
  getYearParams,
  isEmptyMetricValue,
} from 'partner360/features/financialCockpit/ui/cockpitRevenueAndSob/lib/metricPresentation'
import {
  getHierarchyDimensionColumns,
  ICockpitPartnerLinkParams,
  IHierarchyControls,
  IHierarchyExportColumn,
  isExpandableRow,
  renderHierarchyValue,
  withZeroAsEmpty,
} from 'partner360/features/financialCockpit/ui/components/cockpitHierarchyTable'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../../configs/translations'
import {
  EMPTY_COCKPIT_VALUE,
  formatCockpitMoneyDelta,
  formatCockpitPercent,
  formatCockpitPercentChange,
  IPlanQualityHierarchyRow,
  toBadgeScore,
  TRevenueAndSobHierarchyOrder,
} from '../../../../../lib'
import { CockpitTableColumnTitle } from '../../../../components/cockpitTableTitle'
import { CockpitPlanQuality } from '../../../CockpitPlanQuality'

import styles from './getPlanQualityTableColumns.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS

interface IParams {
  fYear: number
  formatMoney: (value: number | null) => string
  showQuota: boolean
  /** Whether the Geo column is shown at all — manager cockpit mode only. */
  showGeoHierarchy: boolean
  /** Which axis is outer. Meaningless when `showGeoHierarchy` is `false` — the caller pins this to
   *  `'productGeo'` in that case. */
  hierarchyOrder: TRevenueAndSobHierarchyOrder
  controls: IHierarchyControls
  /** Short display name for each geo `itemId`, resolved at render time. */
  geoShortNameById: Map<string, string>
  /** Display name for each offering code, resolved at render time. */
  offeringNameByCode: Map<ProgramTypes, string>
  /** Display name for each partner id, resolved at render time. */
  partnerNameById: Map<string, string>
  /** Cockpit context each Partner leaf's link carries into the partner's own page. */
  partnerLinkParams: ICockpitPartnerLinkParams
  /** Resolves the i18n keys the header and the dimension cells show, for the export's plain-text
   *  copy of them. */
  translate: (key: string) => string
}

/** A plain value cell as the export reads it — the same formatted text the column renders. */
const toValueExportCell = (text: string, value: number | null): ICockpitExportCell => ({
  text,
  value,
})

/** Mirrors `renderRate`: the cell's own text *is* the coloured delta here, so it carries the tone
 *  without a separate `delta` beside it. */
const toRateExportCell = (
  value: number | null,
  absolute: number | null,
  isSob: boolean
): ICockpitExportCell => {
  if (isEmptyMetricValue(value)) {
    return { text: EMPTY_COCKPIT_VALUE, value: null }
  }

  const rate = formatCockpitPercentChange(value, isSob)

  return {
    text: isEmptyMetricValue(absolute) ? rate : `${rate} (${formatCockpitMoneyDelta(absolute)})`,
    value,
    deltaTone: getDeltaTone(value),
  }
}

/** The Plan Quality circle's own text and band. `renderScore` hands the circle `toBadgeScore`'s
 *  value — an absent score stands at the 0 it amounts to, a red Low circle — so the export reads
 *  the same value off the same function. */
const toScoreExportCell = (score: number | null): ICockpitExportCell => {
  const shown = toBadgeScore(score)

  return { text: String(shown), value: shown, badge: getQualityBand(shown) }
}

/** The group header is the bare pillar title, sitting centered over its group's columns — hence
 *  `.groupTitle`, which `CockpitTableColumnTitle`'s own block layout doesn't give us. */
const renderGroupHeader = (title: string) => (
  <CockpitTableColumnTitle group className={styles.groupTitle}>
    <Translation text={title} span />
  </CockpitTableColumnTitle>
)

const renderColumnHeader = (title: string) => (
  <CockpitTableColumnTitle>
    <Translation text={title} span />
  </CockpitTableColumnTitle>
)

const renderSubHeader = (title: string, replaceParams?: ReturnType<typeof getYearParams>) => (
  <CockpitTableColumnTitle>
    <span className={styles.subHeader}>
      <Translation text={title} replaceParams={replaceParams} span />
    </span>
  </CockpitTableColumnTitle>
)

/** A rate with the money behind it in brackets — `+3,5% (+18.9K)`. The financial pillar always has
 *  an amount; SOB is a share change with none, so it renders the rate alone. A zero rate means
 *  nothing behind this node, so it reads as the placeholder, like every other column here. */
const renderRate = (value: number | null, absolute: number | null, isSob: boolean) => {
  if (isEmptyMetricValue(value)) {
    return EMPTY_COCKPIT_VALUE
  }

  const rate = formatCockpitPercentChange(value, isSob)
  const text = isEmptyMetricValue(absolute)
    ? rate
    : `${rate} (${formatCockpitMoneyDelta(absolute)})`

  return (
    <span className={classNames(styles.delta, styles[`delta--${getDeltaTone(value)}`])}>
      {text}
    </span>
  )
}

/** A score cell always carries the circle: a row with nothing to score stands at the 0 it amounts
 *  to, rather than dropping to the `--` the rate columns use. `CockpitPlanQuality` bands 0 as
 *  `low`, so it reads as the red circle a genuine zero does. */
export const renderScore = (value: number | null) => (
  <CockpitPlanQuality quality={toBadgeScore(value)} />
)

export const getPlanQualityTableColumns = ({
  fYear,
  formatMoney,
  showQuota,
  showGeoHierarchy,
  hierarchyOrder,
  controls,
  geoShortNameById,
  offeringNameByCode,
  partnerNameById,
  partnerLinkParams,
  translate,
}: IParams): IHierarchyExportColumn<IPlanQualityHierarchyRow>[] => {
  const formatMoneyCell = withZeroAsEmpty(formatMoney)
  const formatPercentCell = withZeroAsEmpty(formatCockpitPercent)

  const baselineYearParams = getYearParams(fYear - 1)
  const yearParams = getYearParams(fYear)

  const isExpandable = (rowId: string) => isExpandableRow(controls, rowId)
  /** The header as it reads once its `{fy}` param is filled in — what `renderSubHeader` shows. */
  const headerText = (title: string, params: ReturnType<typeof getYearParams>) =>
    params.reduce((text, { key, value }) => text.replaceAll(key, value), translate(title))

  const financialQuotaColumn: IHierarchyExportColumn<IPlanQualityHierarchyRow> = {
    field: 'financialQuota',
    parentField: 'financialGroup',
    title: KEYS.revenueAndSob.quota,
    className: styles.valueCell,
    hideSettings: true,
    disableSort: true,
    hideSortIcon: true,
    hideSortIndex: true,
    hideFilterIcon: true,
    titleRender: () => renderSubHeader(KEYS.revenueAndSob.quota, yearParams),
    exportTitle: headerText(KEYS.revenueAndSob.quota, yearParams),
    renderFunction: (item) =>
      renderHierarchyValue(item, formatMoneyCell(item.financial.quota), isExpandable(item.rowId)),
    exportCell: (item) =>
      toValueExportCell(formatMoneyCell(item.financial.quota), item.financial.quota),
  }

  return [
    ...getHierarchyDimensionColumns<IPlanQualityHierarchyRow>({
      showGeoHierarchy,
      hierarchyOrder,
      controls,
      geoShortNameById,
      offeringNameByCode,
      partnerNameById,
      partnerLinkParams,
      translate,
    }),
    {
      field: 'financialGroup',
      title: KEYS.planQuality.financial,
      className: classNames(styles.groupHeader, styles.groupStart, styles.parentHeader),
      hideSettings: true,
      disableSort: true,
      hideSortIcon: true,
      hideSortIndex: true,
      hideFilterIcon: true,
      titleRender: () => renderGroupHeader(KEYS.planQuality.financial),
      exportTitle: translate(KEYS.planQuality.financial),
    },
    {
      field: 'financialBaseline',
      parentField: 'financialGroup',
      title: KEYS.revenueAndSob.baseline,
      className: classNames(styles.valueCell, styles.groupStart),
      hideSettings: true,
      disableSort: true,
      hideSortIcon: true,
      hideSortIndex: true,
      hideFilterIcon: true,
      titleRender: () => renderSubHeader(KEYS.revenueAndSob.baseline, baselineYearParams),
      exportTitle: headerText(KEYS.revenueAndSob.baseline, baselineYearParams),
      exportCell: (item) =>
        toValueExportCell(formatMoneyCell(item.financial.baseline), item.financial.baseline),
      renderFunction: (item) =>
        renderHierarchyValue(
          item,
          formatMoneyCell(item.financial.baseline),
          isExpandable(item.rowId)
        ),
    },
    {
      field: 'financialPlan',
      parentField: 'financialGroup',
      title: KEYS.revenueAndSob.plan,
      className: styles.valueCell,
      hideSettings: true,
      disableSort: true,
      hideSortIcon: true,
      hideSortIndex: true,
      hideFilterIcon: true,
      titleRender: () => renderSubHeader(KEYS.revenueAndSob.plan, yearParams),
      exportTitle: headerText(KEYS.revenueAndSob.plan, yearParams),
      renderFunction: (item) =>
        renderHierarchyValue(item, formatMoneyCell(item.financial.plan), isExpandable(item.rowId)),
      exportCell: (item) =>
        toValueExportCell(formatMoneyCell(item.financial.plan), item.financial.plan),
    },
    ...(showQuota ? [financialQuotaColumn] : []),
    {
      field: 'financialYoy',
      parentField: 'financialGroup',
      title: KEYS.planQuality.yoy,
      className: styles.valueCell,
      hideSettings: true,
      disableSort: true,
      hideSortIcon: true,
      hideSortIndex: true,
      hideFilterIcon: true,
      titleRender: () => renderSubHeader(KEYS.planQuality.yoy),
      renderFunction: (item) => renderRate(item.financial.yoy, item.financial.yoyAbsolute, false),
      exportCell: (item) => toRateExportCell(item.financial.yoy, item.financial.yoyAbsolute, false),
    },
    {
      field: 'financialMarketTarget',
      parentField: 'financialGroup',
      title: KEYS.planQuality.marketTarget,
      className: styles.valueCell,
      hideSettings: true,
      disableSort: true,
      hideSortIcon: true,
      hideSortIndex: true,
      hideFilterIcon: true,
      titleRender: () => renderSubHeader(KEYS.planQuality.marketTarget),
      renderFunction: (item) =>
        renderRate(item.financial.marketTarget, item.financial.marketTargetAbsolute, false),
      exportCell: (item) =>
        toRateExportCell(item.financial.marketTarget, item.financial.marketTargetAbsolute, false),
    },
    {
      field: 'financialScore',
      parentField: 'financialGroup',
      title: KEYS.planQuality.score,
      className: styles.valueCell,
      hideSettings: true,
      disableSort: true,
      hideSortIcon: true,
      hideSortIndex: true,
      hideFilterIcon: true,
      titleRender: () => renderColumnHeader(KEYS.planQuality.score),
      renderFunction: (item) => renderScore(item.financial.score),
      exportCell: (item) => toScoreExportCell(item.financial.score),
    },
    {
      field: 'sobGroup',
      title: KEYS.planQuality.sob,
      className: classNames(styles.groupHeader, styles.groupStart, styles.parentHeader),
      hideSettings: true,
      disableSort: true,
      hideSortIcon: true,
      hideSortIndex: true,
      hideFilterIcon: true,
      titleRender: () => renderGroupHeader(KEYS.planQuality.sob),
      exportTitle: translate(KEYS.planQuality.sob),
    },
    {
      field: 'sobBaseline',
      parentField: 'sobGroup',
      title: KEYS.revenueAndSob.baseline,
      className: classNames(styles.valueCell, styles.groupStart),
      hideSettings: true,
      disableSort: true,
      hideSortIcon: true,
      hideSortIndex: true,
      hideFilterIcon: true,
      titleRender: () => renderSubHeader(KEYS.revenueAndSob.baseline, baselineYearParams),
      exportTitle: headerText(KEYS.revenueAndSob.baseline, baselineYearParams),
      renderFunction: (item) =>
        renderHierarchyValue(item, formatPercentCell(item.sob.baseline), isExpandable(item.rowId)),
      exportCell: (item) =>
        toValueExportCell(formatPercentCell(item.sob.baseline), item.sob.baseline),
    },
    {
      field: 'sobPlan',
      parentField: 'sobGroup',
      title: KEYS.revenueAndSob.plan,
      className: styles.valueCell,
      hideSettings: true,
      disableSort: true,
      hideSortIcon: true,
      hideSortIndex: true,
      hideFilterIcon: true,
      titleRender: () => renderSubHeader(KEYS.revenueAndSob.plan, yearParams),
      exportTitle: headerText(KEYS.revenueAndSob.plan, yearParams),
      renderFunction: (item) =>
        renderHierarchyValue(item, formatPercentCell(item.sob.plan), isExpandable(item.rowId)),
      exportCell: (item) => toValueExportCell(formatPercentCell(item.sob.plan), item.sob.plan),
    },
    {
      field: 'sobYoy',
      parentField: 'sobGroup',
      title: KEYS.planQuality.yoy,
      className: styles.valueCell,
      hideSettings: true,
      disableSort: true,
      hideSortIcon: true,
      hideSortIndex: true,
      hideFilterIcon: true,
      titleRender: () => renderSubHeader(KEYS.planQuality.yoy),
      renderFunction: (item) => renderRate(item.sob.yoy, item.sob.yoyAbsolute, true),
      exportCell: (item) => toRateExportCell(item.sob.yoy, item.sob.yoyAbsolute, true),
    },
    {
      field: 'sobMarketTarget',
      parentField: 'sobGroup',
      title: KEYS.planQuality.marketTarget,
      className: styles.valueCell,
      hideSettings: true,
      disableSort: true,
      hideSortIcon: true,
      hideSortIndex: true,
      hideFilterIcon: true,
      titleRender: () => renderSubHeader(KEYS.planQuality.marketTarget),
      renderFunction: (item) =>
        renderRate(item.sob.marketTarget, item.sob.marketTargetAbsolute, true),
      exportCell: (item) =>
        toRateExportCell(item.sob.marketTarget, item.sob.marketTargetAbsolute, true),
    },
    {
      field: 'sobScore',
      parentField: 'sobGroup',
      title: KEYS.planQuality.score,
      className: styles.valueCell,
      hideSettings: true,
      disableSort: true,
      hideSortIcon: true,
      hideSortIndex: true,
      hideFilterIcon: true,
      titleRender: () => renderColumnHeader(KEYS.planQuality.score),
      renderFunction: (item) => renderScore(item.sob.score),
      exportCell: (item) => toScoreExportCell(item.sob.score),
    },
  ]
}
